import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import { HubTooltip } from '@adflow/frontend/ui-common';
import { theme } from '@adflow/frontend/ui-theme';

import { isExplicitHistoryEmptyValue } from '../util/historyDisplayValue';

import { renderHistoryDisplayValue } from './renderHistoryDisplayValue';

export interface DiffProps extends ComponentPropsWithoutRef<'div'> {
	oldValue: ReactNode;
	newValue: ReactNode;
}

function shouldFormat(value: ReactNode) {
	if (value === null || value === undefined || value === '') {
		return false;
	}

	if (isExplicitHistoryEmptyValue(value)) {
		return false;
	}

	switch (typeof value) {
		case 'object':
		case 'function': {
			return false;
		}
		default: {
			return true;
		}
	}
}

/**
 * Display a change between one value and another.
 * This component will *always* display both values.
 * If they are the same, a better UI would be to
 * show the value without the diff.
 */
export const Diff = forwardRef<HTMLDivElement, DiffProps>(
	function DiffRender(props, ref) {
		const { oldValue, newValue, ...divProps } = props;

		const content = (
			<>
				<span>{renderHistoryDisplayValue(oldValue)}</span> →{' '}
				<span
					css={
						shouldFormat(newValue)
							? { fontWeight: 600, color: theme.colors.text.notice }
							: undefined
					}
				>
					{renderHistoryDisplayValue(newValue)}
				</span>
			</>
		);

		return (
			<HubTooltip title={content}>
				<div {...divProps} ref={ref}>
					{content}
				</div>
			</HubTooltip>
		);
	}
);
